import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useConfig } from '../../hooks/useConfig';
import { logLocationChange } from '../../utilities/analytics';
/**
 * SegmentWrapper component initializes and configures Segment analytics
 * based on the provided configuration and enabled flag.
 *
 * @param {boolean} enabled - Flag to enable or disable Segment initialization.
 * @param {React.ReactNode} children - The child components to be wrapped.
 *
 * @returns {React.ReactNode} The wrapped child components.
 *
 * @remarks
 * This component uses the `useQuery` hook to fetch configuration data and
 * the `useLocation` hook to track URL path changes. When the `enabled` flag
 * is true and the necessary configuration is available, it initializes Segment
 * with the provided segment key and proxy URL. It also logs location changes
 * to Segment whenever the URL path changes.
 */
export const SegmentWrapper = ({ enabled, children, }) => {
    const config = useConfig();
    useEffect(() => {
        if (!config?.segmentKey || !config?.segmentProxyUrl) {
            return;
        }
        const enableSegment = () => {
            if (window?.enableSegment) {
                // initialize segment
                // enableSegment should be defined by the parent application typically in segmentScript.cjs
                // The parent application should also have a graphql endpoint named `config` that returns the segment key and proxy URL
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                window.enableSegment(config.segmentKey, config.segmentProxyUrl);
            }
            else {
                console.error('enableSegment function not found. Make sure the segmentScript.cjs is loaded');
            }
        };
        const disableSegment = () => {
            if (window?.disableSegment) {
                window.disableSegment(config.segmentKey, config.segmentProxyUrl);
            }
            else {
                console.error('disableSegment function not found. Make sure the segmentScript.cjs is loaded');
            }
        };
        if (!enabled) {
            disableSegment();
            return;
        }
        else {
            // if OneTrust is not used, enable tracking is controlled by "enabled" flag
            if (!config?.segmentTrackingOneTrustGroupIds) {
                enableSegment();
                return;
            }
        }
        // ---------------------------------------------------
        // --- OneTrust integration ---
        const initOneTrust = () => {
            const updateTrackingStatus = () => {
                // More information about OneTrust cookie consent codes can be found here:
                // https://my.onetrust.com/articles/en_US/Knowledge/UUID-8102e851-d860-d465-d8d6-b1d636d68eb9
                // C0001 - Strictly Necessary
                // C0002 - Performance
                // C0003 - Functional
                // C0004 - Targeting
                // C0005 - Social Media
                const oneTrustRequiredGroupsIds = config.segmentTrackingOneTrustGroupIds?.split(',');
                // get active groups from OneTrust (comma seperated format), need to filter out empty strings
                const activeGroups = window?.OnetrustActiveGroups.split(',').filter((e) => e);
                // Check if all required cookie group codes are present
                const allRequiredExist = oneTrustRequiredGroupsIds?.every(group => {
                    return activeGroups?.includes(group);
                });
                if (allRequiredExist) {
                    enableSegment();
                }
                else {
                    disableSegment();
                }
            };
            window?.OneTrust?.OnConsentChanged(updateTrackingStatus);
            // Update tracking status on initial load
            updateTrackingStatus();
        };
        let tryAttempts = 0;
        let attemptTimerId = null;
        const tryLoadOneTrust = () => {
            attemptTimerId = null; //  clear attempt timer id
            if (!window?.OneTrust && !window?.OnetrustActiveGroups?.length) {
                console.error('OneTrust not found. Retrying...');
                tryAttempts++;
                // max 5 attempts
                if (tryAttempts < 5) {
                    // try again in 1 second
                    attemptTimerId = setTimeout(tryLoadOneTrust, 5000);
                }
                else {
                    console.error('OneTrust not found. Max attempts reached.');
                }
            }
            else {
                initOneTrust();
            }
        };
        tryLoadOneTrust();
        // cleanup
        return () => {
            if (attemptTimerId) {
                clearTimeout(attemptTimerId);
            }
        };
        // --- OneTrust integration ---
        // ---------------------------------------------------
    }, [enabled, config]);
    const { pathname } = useLocation();
    useEffect(() => {
        logLocationChange(); // tell segment the url path changed
        /* eslint-disable-next-line */
    }, [pathname]);
    return children;
};
